// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { StyleSheet } from 'react-native';
import { useRouter } from 'expo-router';

import GoffyButton from '@/components/GoffyButton';
import { Text, View } from '@/components/Themed';
import { useGuestSignup } from '@/hooks/useGuestSignup';
import { useI18n } from '@/i18n';
import appColors from '@/theme/colors';

type CreateAccountCtaProps = {
  /**
   * 'card' stands on its own in a page; 'button' is the bare action, for a card that already has
   * its own heading and siblings (the start screen's resume card).
   */
  variant?: 'card' | 'button';
  /** Card variant only — the button carries ctaTestID. */
  testID?: string;
  ctaTestID?: string;
  /**
   * Hold the offer back until they hold a claimed player. The invite screen sets this: claiming a
   * player is that screen's whole job, and an account offer beside it competes with the thing the
   * visitor came to do. Everywhere else they have already claimed or chosen to watch.
   */
  requireClaimedPlayer?: boolean;
};

/**
 * The one "create an account" offer, for the visitors who do not have one: a guest who claimed a
 * player from an invite link, and a spectator watching read-only. Both land on /register, which
 * adopts the claimed player when there is one — see the claim → sign-up conversion in
 * UserCreateHandler.
 *
 * Only a guest holding a claim token is promised their scores are kept, because only that token
 * can carry the player across. Everyone else gets the plain offer rather than copy that does not
 * apply to them.
 */
export function CreateAccountCta({
  variant = 'card',
  testID,
  ctaTestID,
  requireClaimedPlayer = false,
}: CreateAccountCtaProps) {
  const { t } = useI18n();
  const router = useRouter();
  const { canCreateAccount, hasClaimedPlayer } = useGuestSignup();

  if (!canCreateAccount || (requireClaimedPlayer && !hasClaimedPlayer)) {
    return null;
  }

  const cta = (
    <GoffyButton
      label={hasClaimedPlayer ? t('claimSignup.cta') : t('menu.createAccount')}
      onPress={() => router.push('/register')}
      // The card IS the offer, so it leads; a bare button sits among a host card's own actions and
      // must not outrank them.
      tone={variant === 'card' ? 'accent' : 'dark'}
      style={styles.button}
      testID={ctaTestID}
    />
  );

  if (variant === 'button') {
    return cta;
  }

  return (
    <View style={styles.card} testID={testID}>
      {hasClaimedPlayer ? (
        <>
          <Text style={styles.title}>{t('claimSignup.title')}</Text>
          <Text style={styles.body}>{t('claimSignup.body')}</Text>
        </>
      ) : null}
      {cta}
    </View>
  );
}

export default CreateAccountCta;

const styles = StyleSheet.create({
  card: {
    width: '100%',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.panel,
    padding: 16,
    gap: 10,
    marginBottom: 12,
  },
  title: {
    fontSize: 18,
    fontWeight: '800',
    color: appColors.text.primary,
  },
  body: {
    fontSize: 13,
    lineHeight: 18,
    color: appColors.text.hint,
  },
  button: {
    width: '100%',
  },
});
